// Auto-generated identifier field with optional manual override. The default
// state is a quiet read-only pill — same visual weight as a value display,
// not a primary input — so the form stays focused on substantive fields.
// Click `Edit` to swap to a real text input that auto-focuses; commit on
// blur, validate uniqueness asynchronously, surface inline errors.
//
// Used for SOW numbers (`SOW-{YYYY}-{NNN}`) on Add SOW; the same primitive
// is reusable for variation numbers. In Edit (inline-expand)
// mode the parent passes `editable={false}` so the pill renders without an
// Edit affordance — SOW numbers are immutable post-creation.
//
// See brand SKILL.md §9.9.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { clsx } from "clsx";

export interface AutoIdentifierFieldProps {
  value: string;
  onCommit: (next: string) => void;
  /** Async uniqueness check. Resolves with `null` when the value is free,
   *  or a human-readable error message when it collides. Called on blur
   *  after a manual edit. Optional — when absent, no async validation runs. */
  validateUnique?: (next: string) => Promise<string | null>;
  /** When false, renders the read-only pill without the Edit affordance.
   *  Used in inline-edit mode where the identifier is locked post-create. */
  editable?: boolean;
  /** Accessible label for the input. Falls back to "Identifier" when omitted. */
  label?: string;
  /** Helper text shown below the field when not editing. The brief default
   *  is "Auto-generated. Click Edit to override." Consumers may override. */
  helperText?: string;
  /** Test hook applied to the wrapper. Inner elements derive child testids. */
  testId?: string;
  /** Initial mode hint — when true, opens directly in edit mode (used when
   *  the form is reopened with a value the user previously typed). */
  startInEditMode?: boolean;
  /** Externally-driven error message from the parent form's validate()
   *  pass. Surfaces with the same styling as the internal async-uniqueness
   *  error. The internal error wins when both are present so a stale
   *  parent error doesn't mask a fresh uniqueness collision the user
   *  is actively correcting. */
  externalError?: string | null;
  disabled?: boolean;
}

export default function AutoIdentifierField({
  value,
  onCommit,
  validateUnique,
  editable = true,
  label = "Identifier",
  helperText,
  testId,
  startInEditMode = false,
  externalError = null,
  disabled = false,
}: AutoIdentifierFieldProps) {
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [draft, setDraft] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the visible pill in sync when the parent commits a new value
  // (e.g. server-assigned number arrives after submit). Pattern 0: adjust
  // during render when the prop changes, not in an effect.
  if (value !== prevValue) {
    setPrevValue(value);
    if (!isEditing) setDraft(value);
  }

  // Auto-focus the input when entering edit mode.
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const handleEditClick = () => {
    setError(null);
    setDraft(value);
    setIsEditing(true);
  };

  const handleInput = (e: FormEvent<HTMLInputElement>) => {
    setDraft(e.currentTarget.value);
    if (error) setError(null);
  };

  const handleBlur = async () => {
    onCommit(draft);
    if (!validateUnique) {
      setIsEditing(false);
      return;
    }
    if (draft.trim() === "") {
      // An empty value is allowed — the backend will auto-generate. Skip
      // the uniqueness check and exit edit mode.
      setIsEditing(false);
      return;
    }
    setIsValidating(true);
    try {
      const result = await validateUnique(draft);
      if (result) {
        setError(result);
        // Stay in edit mode so the user can correct the collision.
        return;
      }
      setIsEditing(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setDraft(value);
      setError(null);
      setIsEditing(false);
      onCommit(value);
    }
  };

  const inputTestId = testId ? `${testId}-input` : undefined;
  const editButtonTestId = testId ? `${testId}-edit` : undefined;

  // Internal error from async uniqueness wins over the parent form's
  // external error so a fresh collision the user is actively correcting
  // doesn't get masked by a stale parent state.
  const visibleError = error ?? externalError ?? null;

  if (isEditing) {
    return (
      <div className="space-y-1" data-testid={testId}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label={label}
          aria-invalid={Boolean(visibleError)}
          aria-busy={isValidating || undefined}
          disabled={disabled}
          data-testid={inputTestId}
          className={clsx(
            "input font-mono",
            visibleError &&
              "border-red-500 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-950",
          )}
        />
        {visibleError && (
          <p
            role="alert"
            className="text-caption text-red-600 dark:text-red-400"
          >
            {visibleError}
          </p>
        )}
        {!visibleError && helperText && (
          <p className="text-caption text-slate-500 dark:text-slate-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="inline-flex items-center gap-2 h-10 px-3 rounded-md bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 font-mono text-body-sm text-slate-700 dark:text-slate-200">
        <span aria-label={label}>{value || "—"}</span>
        {editable && (
          <button
            type="button"
            onClick={handleEditClick}
            disabled={disabled}
            data-testid={editButtonTestId}
            className="ml-auto text-caption font-medium text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm disabled:opacity-50"
          >
            Edit
          </button>
        )}
      </div>
      {visibleError && (
        <p
          role="alert"
          className="text-caption text-red-600 dark:text-red-400"
        >
          {visibleError}
        </p>
      )}
      {!visibleError && helperText && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          {helperText}
        </p>
      )}
    </div>
  );
}
